import axios from '../plugins/axios'
import { Module } from 'vuex'
import { streamProfileAskQuestion } from '@/utils/profileAskStream'
import type {
    ProfileAskConversation,
    ProfileAskConversationListResponse,
    ProfileAskDonePayload,
    ProfileAskMessage,
    ProfileAskMessageListResponse,
} from '@/types/profileAsk'

interface CreateConversationParams {
    workspaceMemberId?: number | string
    title?: string
}

interface ListConversationsParams {
    workspaceMemberId?: number | string
    page?: number
    itemPerPage?: number
}

interface GetMessagesParams {
    conversationId: number
    limit?: number
}

interface AskQuestionParams {
    conversationId: number
    question: string
    signal?: AbortSignal
    onProgress?: (event: string) => void
    onToken?: (content: string) => void
}

interface DeleteConversationParams {
    conversationId: number
}

const profileAskModule: Module<any, any> = {
    actions: {
        async createProfileAskConversation(
            _,
            params: CreateConversationParams = {},
        ): Promise<ProfileAskConversation> {
            const body: Record<string, unknown> = {}
            if (params.title) {
                body.title = params.title
            }
            if (params.workspaceMemberId != null && params.workspaceMemberId !== '') {
                body.workspaceMemberId = Number(params.workspaceMemberId)
            }

            const { data } = await axios.post('/api/profile-ask/conversations', body, {
                silent: true,
            })
            return data
        },

        async listProfileAskConversations(
            _,
            params: ListConversationsParams = {},
        ): Promise<ProfileAskConversationListResponse> {
            const query = new URLSearchParams()
            if (params.page != null) {
                query.set('page', String(params.page))
            }
            if (params.itemPerPage != null) {
                query.set('itemPerPage', String(params.itemPerPage))
            }
            if (params.workspaceMemberId != null && params.workspaceMemberId !== '') {
                query.set('workspaceMemberId', String(params.workspaceMemberId))
            }

            const suffix = query.toString() ? `?${query.toString()}` : ''
            const { data } = await axios.get(`/api/profile-ask/conversations${suffix}`, {
                silent: true,
            })
            return {
                items: data?.['hydra:member'] ?? data?.items ?? [],
                page: data?.page,
                limit: data?.['hydra:itemsPerPage'] ?? data?.limit,
                total: data?.['hydra:totalItems'] ?? data?.total,
            }
        },

        async getProfileAskMessages(
            _,
            params: GetMessagesParams,
        ): Promise<ProfileAskMessage[]> {
            const query = params.limit != null ? `?limit=${params.limit}` : ''
            const { data } = await axios.get<ProfileAskMessageListResponse>(
                `/api/profile-ask/conversations/${params.conversationId}/messages${query}`,
                { silent: true },
            )
            return data?.items ?? data?.['hydra:member'] ?? []
        },

        async askProfileAskQuestion(
            _,
            params: AskQuestionParams,
        ): Promise<ProfileAskDonePayload> {
            return streamProfileAskQuestion({
                conversationId: params.conversationId,
                question: params.question,
                signal: params.signal,
                onProgress: params.onProgress,
                onToken: params.onToken,
            })
        },

        async deleteProfileAskConversation(
            _,
            params: DeleteConversationParams,
        ): Promise<void> {
            await axios.delete(
                `/api/profile-ask/conversations/${params.conversationId}`,
                { silent: true },
            )
        },
    },
}

export default profileAskModule